import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  DescriptionReportReason,
  PlateDescription,
  PlateKnowledgeService,
} from 'src/app/services/plate-knowledge.service';

// Approved plate description in the hero (plate knowledge base, dev2 only).
// Renders nothing when the plate has no approved description. "Seen an
// error?" opens a small report popup (reason required, comment and email
// optional).
@Component({
  selector: 'pla-pdv2-hero-description',
  templateUrl: './hero-description.component.html',
  styleUrls: ['./hero-description.component.scss'],
})
export class HeroDescriptionComponent implements OnChanges {
  @Input() public registration: string = '';
  @Input() public displayRegistration: string = '';

  public description: PlateDescription | null = null;

  public readonly reasons: { key: DescriptionReportReason; label: string }[] = [
    { key: 'incorrect', label: 'Incorrect information' },
    { key: 'reading', label: 'Wrong reading' },
    { key: 'offensive', label: 'Offensive or inappropriate' },
    { key: 'out_of_date', label: 'Out of date' },
    { key: 'spelling', label: 'Spelling or grammar' },
    { key: 'other', label: 'Something else' },
  ];

  public open = false;
  public reason: DescriptionReportReason | null = null;
  public comment = '';
  public email = '';
  public website = ''; // honeypot, hidden from visitors
  public error: string | null = null;
  public sending = false;
  public sent = false;

  @ViewChild('dialog') private dialog: ElementRef<HTMLElement>;
  private returnFocus: HTMLElement | null = null;

  constructor(private knowledge: PlateKnowledgeService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['registration']) {
      const key = this.compactReg;
      this.description = null;
      this.close();
      this.knowledge.get(key).then((kn) => {
        if (key !== this.compactReg) return; // navigated away
        const d = kn && kn.description;
        this.description = d && d.text && d.text.trim() ? d : null;
      });
    }
  }

  private get compactReg(): string {
    return (this.registration || '').replace(/\s+/g, '').toUpperCase();
  }

  public openReport(ev?: Event): void {
    this.returnFocus = (ev && (ev.currentTarget as HTMLElement)) || null;
    this.reason = null;
    this.comment = '';
    this.email = '';
    this.website = '';
    this.error = null;
    this.sending = false;
    this.sent = false;
    this.open = true;
    setTimeout(() => {
      const first = this.dialog && this.dialog.nativeElement.querySelector('button');
      if (first) (first as HTMLElement).focus();
    });
  }

  public close(): void {
    if (!this.open) return;
    this.open = false;
    if (this.returnFocus) this.returnFocus.focus();
    this.returnFocus = null;
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    this.close();
  }

  public pick(key: DescriptionReportReason): void {
    this.reason = key;
    this.error = null;
  }

  public send(): void {
    if (this.sending || !this.description) return;
    if (!this.reason) {
      this.error = 'Please choose what is wrong.';
      return;
    }
    const email = this.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.error = 'That email address does not look right.';
      return;
    }
    this.error = null;
    this.sending = true;
    this.knowledge
      .report({
        reg: this.compactReg,
        note_id: this.description.note_id,
        version: this.description.version,
        reason: this.reason,
        comment: this.comment.trim() || undefined,
        email: email || undefined,
        website: this.website,
      })
      .then((res) => {
        this.sending = false;
        if (res.ok) this.sent = true;
        else this.error = res.error || 'Sorry, that did not send. Please try again.';
      });
  }
}
